import { borderRadius } from '@eversports/design-tokens/border-radius'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Box from '../Box'
import { buttonVariants } from '../Button'

const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/

interface Props {
  encodedTo: string
  external?: boolean
  blank?: boolean
  ariaLabel?: string
  buttonVariant?: keyof typeof buttonVariants
  dataTestId?: string
}

export interface HideFromCrawlerProps extends Props {
  type: 'hidden'
}

export interface SEOLinkProps extends Props {
  type: 'crawler'
  crawlerLink: string
}

const SeoLink: React.FC<React.PropsWithChildren<HideFromCrawlerProps | SEOLinkProps>> = (props) => {
  const { children, external = false, blank = false, encodedTo, ariaLabel, type, buttonVariant, dataTestId } = props

  // Check that the url is encoded and not seen by Google
  if (!base64regex.test(encodedTo)) {
    throw new Error(`The SEO url is not base64 encoded: ${encodedTo}`)
  }

  const initialTo = type === 'crawler' ? props.crawlerLink : '#'

  const [to, setTo] = useState(initialTo)
  const linkElRef = useRef<HTMLAnchorElement | null>(null)

  const blankProps = blank ? { target: `_blank`, rel: 'noopener' } : {}
  const ariaLabelProps = ariaLabel ? { 'aria-label': ariaLabel } : {}

  useEffect(() => {
    if (!linkElRef.current) return

    const handleFirstInteraction = () => setTo(atob(encodedTo))

    linkElRef.current.addEventListener('touchstart', handleFirstInteraction, { once: true })
    linkElRef.current.addEventListener('mousedown', handleFirstInteraction, { once: true })
    linkElRef.current.addEventListener('onfocus', handleFirstInteraction, { once: true })
    linkElRef.current.addEventListener('mousemove', handleFirstInteraction, { once: true })
    linkElRef.current.addEventListener('keydown', handleFirstInteraction, { once: true })

    return () => {
      if (!linkElRef.current) return
      linkElRef.current.removeEventListener('touchstart', handleFirstInteraction)
      linkElRef.current.removeEventListener('mousedown', handleFirstInteraction)
      linkElRef.current.removeEventListener('onfocus', handleFirstInteraction)
      linkElRef.current.removeEventListener('mousemove', handleFirstInteraction)
      linkElRef.current.removeEventListener('keydown', handleFirstInteraction)
    }
  }, [])

  if (buttonVariant) {
    // Many of our links look like buttons
    // We can't have a link wrap a button - so we can optionally style links to look like buttons
    // Otherwise, it's semantically off, and if you use the keyboard to navigate, it'll double focus (once on the link, once on the button)
    const variantStyles = {
      ...buttonVariants[buttonVariant].sx,
      borderRadius: `${borderRadius.button}px`,
      width: '100%',
      '& > a': {
        paddingX: 4,
        paddingY: 2,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    }
    return external ? (
      <Box ref={linkElRef} sx={{ ...variantStyles }} data-testid={dataTestId}>
        <a href={to} {...blankProps} {...ariaLabelProps}>
          {children}
        </a>
      </Box>
    ) : (
      <Box ref={linkElRef} sx={{ ...variantStyles }} data-testid={dataTestId}>
        <RouterLink to={to} {...ariaLabelProps} {...blankProps}>
          {children}
        </RouterLink>
      </Box>
    )
  }

  return external ? (
    <a ref={linkElRef} href={to} {...blankProps} {...ariaLabelProps} data-testid={dataTestId}>
      {children}
    </a>
  ) : (
    <RouterLink ref={linkElRef} to={to} {...ariaLabelProps} {...blankProps} data-testid={dataTestId}>
      {children}
    </RouterLink>
  )
}

export default SeoLink
